import React from 'react';
import Skeleton from 'react-loading-skeleton';
import SelectMe from 'react-select-me';

import Spinner from '../../common/Spinner';

export type Filters = {
  periode: 'all' | 'current-month' | 'current-quarter' | 'next-month';
  gestionnaire: 'all' | string;
  etatDate: 'all' | 'qualified' | 'not-qualified';
  presence: 'all' | 'confirmed' | 'declined' | 'pending';
  search: string;
};

interface FiltersBarProps {
  filters: Filters;
  gestionnaireOptions: string[];
  onChange: (next: Filters) => void;
  onReset: () => void;
  yearsOptions?: Array<{ value: string; label: string }>;
  selectedYears?: number[];
  onYearsChange?: (years: number[]) => void;
  onFetchYearsClick?: () => void;
  canFetchYears?: boolean;
  isFetchingYears?: boolean;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  filters,
  gestionnaireOptions,
  onChange,
  onReset,
  yearsOptions = [],
  selectedYears = [],
  onYearsChange,
  onFetchYearsClick,
  canFetchYears = false,
  isFetchingYears = false,
}) => {
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-100 to-slate-50 p-4">
      <div className="space-y-4 2xl:space-y-0">
        {/* First group of filters */}
        <div className="space-y-2 md:flex md:min-w-[260px] md:items-center md:space-y-0 md:space-x-2">
          {onYearsChange && yearsOptions.length > 0 ? (
            <>
              <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">Années concernées dans les ventes (appliquées aux statistiques et au tableau) :</label>
              <div className="min-w-[220px]">
                <SelectMe
                  id="ga-years-select"
                  name="years"
                  value={selectedYears.map((y) => ({ value: String(y), label: String(y) }))}
                  onChange={(options) => onYearsChange(options.map((o) => Number(o.value)))}
                  options={yearsOptions}
                  multiple
                  closeOnSelect={false}
                  listPosition="bottom"
                  placeholder="Sélectionnez une ou plusieurs années"
                />
              </div>
            </>
          ) : 
            <Skeleton count={1} height={30} />
          }
        </div>
        {/* Centered button to fetch sales years data */}
        {onFetchYearsClick && (
          <div className="flex w-full justify-center">
            <button
              type="button"
              onClick={onFetchYearsClick}
              className={`rounded-md px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60 ${isFetchingYears ? 'cursor-wait !bg-blue-400' : canFetchYears ? 'cursor-pointer bg-blue-600 hover:bg-blue-700' : 'bg-blue-300'}`}
              disabled={!canFetchYears || isFetchingYears}
            >
              {isFetchingYears ? (
                <span className="inline-flex items-center space-x-2">
                  <Spinner size="sm" />
                  <span>Chargement…</span>
                </span>
              ) : (
                <span>Récupérer les données des années de ventes</span>
              )}
            </button>
          </div>
        )}
        {/* Separator  */}
        <div className="my-4 flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* All filters layout: stacked on small, 2x2 grid on medium, single row on 2xl */}
        <div className="space-y-3 md:grid md:grid-cols-2 md:gap-4">
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">Période AG :</label>
            <select
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto"
              value={filters.periode}
              onChange={(e) => onChange({ ...filters, periode: e.target.value as Filters['periode'] })}
            >
              <option value="all">Toutes</option>
              <option value="current-month">AG du mois</option>
              <option value="current-quarter">AG du trimestre</option>
              <option value="next-month">AG mois prochain</option>
            </select>
          </div>
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">Gestionnaire :</label>
            <select
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto md:min-w-[160px]"
              value={filters.gestionnaire}
              onChange={(e) => onChange({ ...filters, gestionnaire: e.target.value })}
            >
              <option value="all">Tous</option>
              {gestionnaireOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">État dates AG :</label>
            <select
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto"
              value={filters.etatDate}
              onChange={(e) => onChange({ ...filters, etatDate: e.target.value as Filters['etatDate'] })}
            >
              <option value="all">Tous</option>
              <option value="qualified">Qualifiées (avec date)</option>
              <option value="not-qualified">Non qualifiées (sans date)</option>
            </select>
          </div>
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">Présence AG :</label>
            <select
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto"
              value={filters.presence}
              onChange={(e) => onChange({ ...filters, presence: e.target.value as Filters['presence'] })}
            >
              <option value="all">Tous</option>
              <option value="confirmed">Présent</option>
              <option value="declined">Absent</option>
              <option value="pending">À confirmer</option>
            </select>
          </div>
        </div>

        {/* Search and reset button group */}
        <div className="space-y-3 md:flex md:items-center md:space-y-0 md:space-x-4 2xl:flex-1">
          <div className="space-y-2 md:flex md:flex-1 md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">Rechercher :</label>
            <input
              className="block w-full rounded-md border border-slate-300 p-2 text-sm"
              value={filters.search}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              placeholder="Nom, adresse..."
            />
          </div>
          <div className="flex justify-end md:justify-start">
            <button onClick={onReset} className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700">
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltersBar;


